/**
 * Scheda contratto — creazione e censimento.
 *
 * Tre cose che questa pagina fa di proposito, e che vanno capite prima di modificarla.
 *
 * **1. La data di disdetta non si scrive: si mostra.** E' un derivato (`dataFine − preavviso`) che
 * il backend ricalcola a ogni scrittura e persiste, perche' ci filtrano le liste e ci gira sopra
 * il job disdette. Qui c'e' solo un'anteprima, con la stessa aritmetica del server (UTC sulla sola
 * parte di giorno): un campo scrivibile permetterebbe di salvare una data che la scrittura
 * successiva sovrascriverebbe comunque.
 *
 * **2. `rsaIds` e' autoritativo.** Se lo si manda, il backend riconcilia la N:N e porta il lookup
 * singolo `phc_RSA` sulla prima dell'elenco. Per questo l'ordine conta e la prima e' etichettata
 * «principale»: non e' un dettaglio estetico, e' quale RSA vedra' il portale manutenzioni.
 *
 * Le **tipologie coperte** seguono la stessa regola (ADR-0006): `tipologieIds` e' autoritativo e
 * la prima e' la principale. Un contratto di manutenzione impianti copre piu' servizi insieme.
 *
 * **3. I default della tipologia si propongono, non si impongono.** `durataStandardMesi` e
 * `preavvisoStandardGiorni` esistono su `phc_tipologiafornitura` e sono oggi tutti null; quando
 * qualcuno li valorizzera', proporli su un contratto nuovo evita di ridigitarli. Su un contratto
 * gia' esistente non si toccano mai: sovrascriverebbero un dato censito con uno standard.
 */

import React, { useEffect, useMemo, useState } from "react";
import {
  useNavigate,
  useParams,
  Link,
  useSearchParams,
} from "react-router-dom";
import { useAuth } from "@shared/context/AuthContext";
import { useToast } from "@shared/components/ui/Toast";
import Modal from "@shared/components/ui/Modal";
import {
  contrattiApi,
  fornitoriApi,
  anagraficheApi,
} from "../services/acquistiApi";
import {
  Campo,
  Testo,
  AreaTesto,
  Data,
  Numero,
  Scelta,
  Interruttore,
  Avviso,
  Caricamento,
  SceltaMultipla,
} from "../components/campi";
import {
  STATO_CONTRATTO,
  TIPO_IMPORTO,
  BASE_PAGAMENTO,
  MODALITA_FATTURAZIONE,
  FATTURAZIONE_CON_DETTAGLIO,
  MODALITA_RINNOVO,
  RINNOVO_TACITO_CON_DISDETTA,
  AMBITO,
  INDICIZZAZIONE,
  FORMA_COMUNICAZIONE,
  opzioni,
} from "../constants/optionSet";
import { dataInput, dataIt, giorniA } from "../utils/formato";
import Documenti from "../components/Documenti";
import ChiusuraContratto from "../components/ChiusuraContratto";

/**
 * Stessa aritmetica di `calcolaDataDisdetta` nel backend: UTC, sola parte di giorno, e `null`
 * quando manca una delle due fonti. Se una delle due cambia, vanno cambiate entrambe — questa e'
 * un'anteprima del calcolo del server, non un secondo calcolo con regole proprie.
 */
function anteprimaDisdetta(dataFine, giorniPreavviso) {
  if (!dataFine || giorniPreavviso === "" || giorniPreavviso == null)
    return null;
  const giorni = Number(giorniPreavviso);
  if (!Number.isFinite(giorni)) return null;
  const d = new Date(`${String(dataFine).slice(0, 10)}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return null;
  d.setUTCDate(d.getUTCDate() - giorni);
  return d.toISOString().slice(0, 10);
}

const VUOTO = {
  nome: "",
  codice: "",
  fornitoreId: "",
  tipologiaFornituraId: "",
  stato: "",
  dataDecorrenza: "",
  dataFine: "",
  durataMesi: "",
  modalitaRinnovo: "",
  rinnovoMesi: "",
  giorniPreavviso: "",
  importoAnnuo: "",
  importoMassimo: "",
  tipoImporto: "",
  basePagamento: "",
  giorniPagamento: "",
  modalitaFatturazione: "",
  dettaglioFatturazione: "",
  indicizzazione: "",
  ambito: "",
  accordoQuadro: false,
  esclusiva: false,
  nomeReferente: "",
  emailReferente: "",
  telefonoReferente: "",
  orariPreferenziali: "",
  noteSla: "",
  clausoleParticolari: "",
  urlDocumento: "",
  note: "",
  articoloDisdetta: "",
  articoloRecesso: "",
  formaComunicazione: "",
  preavvisoLavorativi: false,
};

export default function ContrattoPage() {
  const { id } = useParams();
  const nuovo = !id;
  const navigate = useNavigate();
  // `?chiusura=1` (o `=A|B|C|D`) arriva dalla lista contratti e dal digest dei promemoria: apre il
  // pannello di chiusura al caricamento, sul modello indicato o su quello proposto.
  const [searchParams] = useSearchParams();
  const apriChiusura = searchParams.get("chiusura");
  const [versioneDocumenti, setVersioneDocumenti] = useState(0);
  const { showToast } = useToast();
  const { canPerformAction, user } = useAuth();
  const puoScrivere = canPerformAction("contratti.gestisci");
  const isAdmin = user?.ruolo === 100000000;

  const [form, setForm] = useState(VUOTO);
  const [rsaIds, setRsaIds] = useState([]);
  const [infoEsclusiva, setInfoEsclusiva] = useState(false);
  const [tipologieIds, setTipologieIds] = useState([]);
  const [dettaglio, setDettaglio] = useState(null);
  const [caricamento, setCaricamento] = useState(!nuovo);
  const [salvataggio, setSalvataggio] = useState(false);
  const [errore, setErrore] = useState(null);

  const [fornitori, setFornitori] = useState([]);
  const [tipologie, setTipologie] = useState([]);
  const [rsa, setRsa] = useState([]);

  useEffect(() => {
    Promise.allSettled([
      fornitoriApi.list(),
      anagraficheApi.tipologieFornitura(),
      anagraficheApi.rsa(),
    ]).then(([f, t, r]) => {
      if (f.status === "fulfilled") setFornitori(f.value.value || []);
      if (t.status === "fulfilled") setTipologie(t.value.value || []);
      if (r.status === "fulfilled") setRsa(r.value.value || []);
    });
  }, []);

  useEffect(() => {
    if (nuovo) return;
    let vivo = true;
    setCaricamento(true);
    contrattiApi
      .get(id)
      .then((c) => {
        if (!vivo) return;
        setDettaglio(c);
        setForm({
          nome: c.nome ?? "",
          codice: c.codice ?? "",
          fornitoreId: c.fornitore?.id ?? "",
          tipologiaFornituraId: c.tipologiaFornitura?.id ?? "",
          stato: c.stato ?? "",
          dataDecorrenza: dataInput(c.dataDecorrenza),
          dataFine: dataInput(c.dataFine),
          durataMesi: c.durataMesi ?? "",
          modalitaRinnovo: c.modalitaRinnovo ?? "",
          rinnovoMesi: c.rinnovoMesi ?? "",
          giorniPreavviso: c.giorniPreavviso ?? "",
          importoAnnuo: c.importoAnnuo ?? "",
          importoMassimo: c.importoMassimo ?? "",
          tipoImporto: c.tipoImporto ?? "",
          basePagamento: c.basePagamento ?? "",
          giorniPagamento: c.giorniPagamento ?? "",
          modalitaFatturazione: c.modalitaFatturazione ?? "",
          dettaglioFatturazione: c.dettaglioFatturazione ?? "",
          indicizzazione: c.indicizzazione ?? "",
          ambito: c.ambito ?? "",
          accordoQuadro: !!c.accordoQuadro,
          esclusiva: !!c.esclusiva,
          nomeReferente: c.nomeReferente ?? "",
          emailReferente: c.emailReferente ?? "",
          telefonoReferente: c.telefonoReferente ?? "",
          orariPreferenziali: c.orariPreferenziali ?? "",
          noteSla: c.noteSla ?? "",
          clausoleParticolari: c.clausoleParticolari ?? "",
          urlDocumento: c.urlDocumento ?? "",
          note: c.note ?? "",
          articoloDisdetta: c.articoloDisdetta ?? "",
          articoloRecesso: c.articoloRecesso ?? "",
          formaComunicazione: c.formaComunicazione ?? "",
          preavvisoLavorativi: !!c.preavvisoLavorativi,
        });
        setRsaIds((c.rsaCoperte || []).map((r) => r.id));
        setTipologieIds((c.tipologieCoperte || []).map((t) => t.id));
      })
      .catch((e) => setErrore(e.message || "Contratto non trovato"))
      .finally(() => vivo && setCaricamento(false));
    return () => {
      vivo = false;
    };
  }, [id, nuovo]);

  const set = (chiave) => (valore) =>
    setForm((f) => ({ ...f, [chiave]: valore }));

  /** Solo in creazione: propone i default della tipologia principale sui campi ancora vuoti. */
  const cambiaTipologie = (ids) => {
    setTipologieIds(ids);
    const tipologiaId = ids[0] ?? "";
    setForm((f) => {
      const prossimo = { ...f, tipologiaFornituraId: tipologiaId };
      if (!nuovo) return prossimo;
      const t = tipologie.find((x) => x.id === tipologiaId);
      if (!t) return prossimo;
      if (prossimo.durataMesi === "" && t.durataStandardMesi != null)
        prossimo.durataMesi = t.durataStandardMesi;
      if (prossimo.giorniPreavviso === "" && t.preavvisoStandardGiorni != null)
        prossimo.giorniPreavviso = t.preavvisoStandardGiorni;
      return prossimo;
    });
  };

  const disdetta = useMemo(
    () => anteprimaDisdetta(form.dataFine, form.giorniPreavviso),
    [form.dataFine, form.giorniPreavviso],
  );

  const richiedeCapitolato = tipologie.some(
    (t) => tipologieIds.includes(t.id) && t.richiedeCapitolato,
  );

  const salva = async (e) => {
    e.preventDefault();
    if (!puoScrivere) return;
    setSalvataggio(true);
    setErrore(null);
    try {
      const body = {
        ...form,
        tipologiaFornituraId: tipologieIds[0] ?? "",
        rsaIds,
        tipologieIds,
      };
      if (nuovo) {
        const res = await contrattiApi.create(body);
        showToast("Contratto creato", "success");
        navigate(`/contratti/${res.id}`, { replace: true });
      } else {
        await contrattiApi.update(id, body);
        showToast("Contratto aggiornato", "success");
        const aggiornato = await contrattiApi.get(id);
        setDettaglio(aggiornato);
      }
    } catch (err) {
      setErrore(err.data?.message || err.message || "Salvataggio fallito");
    } finally {
      setSalvataggio(false);
    }
  };

  const elimina = async () => {
    if (
      !window.confirm(
        "Eliminare il contratto? Si perde lo storico e si spezza la catena " +
          "«contratto precedente» di chi lo referenzia. Per smettere di usarlo, " +
          "portarlo a stato Dismesso.",
      )
    )
      return;
    try {
      await contrattiApi.remove(id);
      showToast("Contratto eliminato", "success");
      navigate("/contratti");
    } catch (err) {
      setErrore(err.data?.message || err.message);
    }
  };

  const toggleRsa = (rsaId) =>
    setRsaIds((ids) =>
      ids.includes(rsaId) ? ids.filter((x) => x !== rsaId) : [...ids, rsaId],
    );

  if (caricamento) return <Caricamento testo="Caricamento contratto…" />;

  return (
    <form onSubmit={salva} className="space-y-4 max-w-5xl">
      <div className="flex flex-wrap items-center gap-3">
        <Link
          to="/contratti"
          className="text-sm text-acquisti-700 hover:underline"
        >
          ← Contratti
        </Link>
        <h1 className="text-xl font-semibold text-gray-900">
          {nuovo ? "Nuovo contratto" : form.nome || "(senza nome)"}
        </h1>
        <div className="ml-auto flex gap-2">
          {!nuovo && isAdmin && (
            <button type="button" className="btn-danger" onClick={elimina}>
              Elimina
            </button>
          )}
          {puoScrivere && (
            <button
              type="submit"
              className="btn-primary"
              disabled={salvataggio}
            >
              {salvataggio ? "Salvataggio…" : nuovo ? "Crea" : "Salva"}
            </button>
          )}
        </div>
      </div>

      {errore && <Avviso tipo="errore">{errore}</Avviso>}

      {!puoScrivere && (
        <Avviso tipo="info">
          Hai accesso in sola lettura: l'azione <code>contratti.gestisci</code>{" "}
          non è concessa al tuo ruolo.
        </Avviso>
      )}

      <fieldset disabled={!puoScrivere} className="space-y-4">
        <section className="card space-y-4">
          <h2 className="font-semibold text-gray-900">Identificazione</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Campo label="Nome del contratto *" className="md:col-span-2">
              <Testo valore={form.nome} onChange={set("nome")} required />
            </Campo>
            <Campo label="Codice contratto">
              <Testo valore={form.codice} onChange={set("codice")} />
            </Campo>
            <Campo label="Stato">
              <Scelta
                valore={form.stato}
                onChange={set("stato")}
                opzioni={opzioni(STATO_CONTRATTO)}
              />
            </Campo>
            <Campo label="Fornitore">
              <Scelta
                valore={form.fornitoreId}
                onChange={set("fornitoreId")}
                opzioni={fornitori.map((f) => ({ value: f.id, label: f.nome }))}
              />
            </Campo>
            <Campo
              label="Tipologie di fornitura (servizi coperti)"
              className="md:col-span-2"
              aiuto={
                (richiedeCapitolato
                  ? "Almeno una di queste tipologie richiede un capitolato. "
                  : "") +
                "La principale è quella che vede il portale manutenzioni."
              }
            >
              <SceltaMultipla
                valori={tipologieIds}
                onChange={cambiaTipologie}
                opzioni={tipologie.map((t) => ({ value: t.id, label: t.nome }))}
                disabled={!puoScrivere}
              />
            </Campo>
            <Campo label="Ambito">
              <Scelta
                valore={form.ambito}
                onChange={set("ambito")}
                opzioni={opzioni(AMBITO)}
              />
            </Campo>
            <div className="flex items-end gap-6 pb-1">
              <Interruttore
                valore={form.accordoQuadro}
                onChange={set("accordoQuadro")}
                testo="Accordo quadro"
              />
              <Interruttore
                valore={form.esclusiva}
                onChange={(v) => {
                  set("esclusiva")(v);
                  // Si spiega quando lo si accende: e' il momento in cui si sta per affermarlo.
                  if (v) setInfoEsclusiva(true);
                }}
                testo="Esclusiva"
              />
              <button
                type="button"
                className="text-xs text-acquisti-700 hover:underline"
                onClick={() => setInfoEsclusiva(true)}
              >
                cosa significa?
              </button>
            </div>
            <Modal
              isOpen={infoEsclusiva}
              onClose={() => setInfoEsclusiva(false)}
              title="Contratto in esclusiva"
              size="md"
              footer={
                <button
                  type="button"
                  className="btn-primary"
                  onClick={() => setInfoEsclusiva(false)}
                >
                  Ho capito
                </button>
              }
            >
              <div className="space-y-3 text-sm text-gray-700">
                <p>
                  Il contratto prevede che{" "}
                  <strong>questo fornitore sia l'unico</strong> a cui PHC può
                  affidare i servizi coperti dal contratto, nelle RSA indicate e
                  per tutta la sua durata.
                </p>
                <p>
                  In pratica, finché il contratto è in vigore, affidare le
                  stesse prestazioni a un altro fornitore su quelle sedi — anche
                  per un singolo intervento — può essere una violazione del
                  contratto, salvo le eccezioni previste dalle clausole.
                </p>
                <p>
                  Va attivato{" "}
                  <strong>solo se l'esclusiva è scritta nel contratto</strong>.
                  In quel caso conviene riportare nelle clausole particolari il
                  perimetro (servizi, sedi) e le eventuali eccezioni (urgenze,
                  prestazioni non coperte).
                </p>
                <p className="text-xs text-gray-500">
                  Il portale lo registra come informazione: oggi non blocca gare
                  o interventi di altri fornitori.
                </p>
              </div>
            </Modal>
          </div>
        </section>

        <section className="card space-y-4">
          <h2 className="font-semibold text-gray-900">
            Durata, rinnovo, disdetta
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Campo label="Data decorrenza">
              <Data
                valore={form.dataDecorrenza}
                onChange={set("dataDecorrenza")}
              />
            </Campo>
            <Campo label="Data fine">
              <Data valore={form.dataFine} onChange={set("dataFine")} />
            </Campo>
            <Campo label="Durata (mesi)">
              <Numero valore={form.durataMesi} onChange={set("durataMesi")} />
            </Campo>
            <Campo label="Modalità di rinnovo">
              <Scelta
                valore={form.modalitaRinnovo}
                onChange={set("modalitaRinnovo")}
                opzioni={opzioni(MODALITA_RINNOVO)}
              />
            </Campo>
            <Campo label="Rinnovo (mesi)">
              <Numero valore={form.rinnovoMesi} onChange={set("rinnovoMesi")} />
            </Campo>
            <Campo
              label="Giorni di preavviso"
              aiuto="Senza preavviso non esiste una data di disdetta, e nessuna notifica partirà."
            >
              <Numero
                valore={form.giorniPreavviso}
                onChange={set("giorniPreavviso")}
                min="0"
              />
            </Campo>
            <Campo
              label="Articolo su durata, rinnovo e disdetta"
              aiuto="Dal contratto firmato, es. «art. 6». Lo cita la PEC di disdetta."
            >
              <Testo
                valore={form.articoloDisdetta}
                onChange={set("articoloDisdetta")}
                placeholder="art. …"
              />
            </Campo>
            <Campo
              label="Articolo sul recesso anticipato"
              aiuto="Solo se il contratto prevede il recesso con preavviso."
            >
              <Testo
                valore={form.articoloRecesso}
                onChange={set("articoloRecesso")}
                placeholder="art. …"
              />
            </Campo>
            <Campo label="Forma richiesta per la disdetta">
              <Scelta
                valore={form.formaComunicazione}
                onChange={set("formaComunicazione")}
                opzioni={opzioni(FORMA_COMUNICAZIONE)}
              />
            </Campo>
            <div className="flex items-end pb-1 md:col-span-3">
              <Interruttore
                valore={form.preavvisoLavorativi}
                onChange={set("preavvisoLavorativi")}
                testo="Il preavviso è in giorni lavorativi (la data calcolata è indicativa: il termine reale è prima)"
              />
            </div>
          </div>

          <div className="rounded-md bg-gray-50 border border-gray-200 px-4 py-3 text-sm">
            <span className="text-gray-600">Disdetta entro (calcolata): </span>
            {disdetta ? (
              <>
                <strong>{dataIt(disdetta)}</strong>
                <span className="text-gray-500">
                  {" "}
                  — {giorniA(disdetta)} giorni da oggi
                </span>
              </>
            ) : (
              <span className="text-yellow-800">
                non calcolabile: servono sia la data di fine sia i giorni di
                preavviso
              </span>
            )}
            {Number(form.modalitaRinnovo) === RINNOVO_TACITO_CON_DISDETTA &&
              !disdetta && (
                <div className="mt-2 text-yellow-900">
                  Il rinnovo è <strong>tacito con disdetta</strong>: senza
                  preavviso questo contratto si rinnoverà da solo e nessuno
                  verrà avvisato.
                </div>
              )}
          </div>
        </section>

        <section className="card space-y-4">
          <h2 className="font-semibold text-gray-900">Economia</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Campo label="Importo annuo (€)">
              <Numero
                valore={form.importoAnnuo}
                onChange={set("importoAnnuo")}
                step="0.01"
              />
            </Campo>
            <Campo label="Importo massimo (€)">
              <Numero
                valore={form.importoMassimo}
                onChange={set("importoMassimo")}
                step="0.01"
              />
            </Campo>
            <Campo label="Tipo importo">
              <Scelta
                valore={form.tipoImporto}
                onChange={set("tipoImporto")}
                opzioni={opzioni(TIPO_IMPORTO)}
              />
            </Campo>
            <Campo
              label="Modalità di fatturazione"
              aiuto="Come si fattura il contratto: rate, SAL, unica soluzione, a consumo."
            >
              <Scelta
                valore={form.modalitaFatturazione}
                onChange={set("modalitaFatturazione")}
                opzioni={opzioni(MODALITA_FATTURAZIONE)}
              />
            </Campo>
            <Campo
              label="Dettaglio fatturazione"
              className="md:col-span-2"
              aiuto={
                FATTURAZIONE_CON_DETTAGLIO.includes(
                  Number(form.modalitaFatturazione),
                )
                  ? "Descrivi il criterio: SAL e percentuali o milestone, oppure come si calcolano le rate."
                  : "Facoltativo: anticipi, split delle rate, eccezioni (es. «prima rata anticipata»)."
              }
            >
              <AreaTesto
                valore={form.dettaglioFatturazione}
                onChange={set("dettaglioFatturazione")}
                righe={2}
                placeholder={
                  Number(form.modalitaFatturazione) === 100000006
                    ? "es. 30% all'avvio, 40% al SAL intermedio, 30% al collaudo"
                    : undefined
                }
              />
            </Campo>
            <Campo label="Base pagamento">
              <Scelta
                valore={form.basePagamento}
                onChange={set("basePagamento")}
                opzioni={opzioni(BASE_PAGAMENTO)}
              />
            </Campo>
            <Campo label="Giorni pagamento">
              <Numero
                valore={form.giorniPagamento}
                onChange={set("giorniPagamento")}
              />
            </Campo>
            <Campo label="Indicizzazione">
              <Scelta
                valore={form.indicizzazione}
                onChange={set("indicizzazione")}
                opzioni={opzioni(INDICIZZAZIONE)}
              />
            </Campo>
          </div>
        </section>

        <section className="card space-y-3">
          <h2 className="font-semibold text-gray-900">RSA coperte</h2>
          {rsaIds.length > 0 && (
            <p className="text-sm">
              Principale:{" "}
              <strong>
                {rsa.find((r) => r.id === rsaIds[0])?.nome || rsaIds[0]}
              </strong>
            </p>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
            {rsa.map((r) => (
              <label
                key={r.id}
                className="flex items-center gap-2 text-sm text-gray-700"
              >
                <input
                  type="checkbox"
                  className="rounded border-gray-300 text-acquisti-600 focus:ring-acquisti-500"
                  checked={rsaIds.includes(r.id)}
                  onChange={() => toggleRsa(r.id)}
                />
                {r.nome}
              </label>
            ))}
          </div>
          {rsa.length === 0 && (
            <p className="text-sm text-gray-500">Nessuna RSA disponibile.</p>
          )}
        </section>

        <section className="card space-y-4">
          <h2 className="font-semibold text-gray-900">Referente e documenti</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Campo label="Nome referente">
              <Testo
                valore={form.nomeReferente}
                onChange={set("nomeReferente")}
              />
            </Campo>
            <Campo label="Email referente">
              <Testo
                valore={form.emailReferente}
                onChange={set("emailReferente")}
                type="email"
              />
            </Campo>
            <Campo label="Telefono referente">
              <Testo
                valore={form.telefonoReferente}
                onChange={set("telefonoReferente")}
              />
            </Campo>
            <Campo label="URL documento" className="md:col-span-3">
              <Testo
                valore={form.urlDocumento}
                onChange={set("urlDocumento")}
                placeholder="https://…"
              />
            </Campo>
            <Campo label="Orari preferenziali" className="md:col-span-3">
              <Testo
                valore={form.orariPreferenziali}
                onChange={set("orariPreferenziali")}
              />
            </Campo>
            <Campo label="Note SLA" className="md:col-span-3">
              <AreaTesto valore={form.noteSla} onChange={set("noteSla")} />
            </Campo>
            <Campo label="Clausole particolari" className="md:col-span-3">
              <AreaTesto
                valore={form.clausoleParticolari}
                onChange={set("clausoleParticolari")}
              />
            </Campo>
            <Campo label="Note" className="md:col-span-3">
              <AreaTesto valore={form.note} onChange={set("note")} />
            </Campo>
          </div>
        </section>
      </fieldset>

      {dettaglio && (
        <ChiusuraContratto
          contrattoId={id}
          dettaglio={dettaglio}
          puoScrivere={puoScrivere}
          apriSubito={apriChiusura}
          onDocumentoGenerato={() => setVersioneDocumenti((v) => v + 1)}
          onRegistrato={async () => {
            const aggiornato = await contrattiApi.get(id);
            setDettaglio(aggiornato);
            setForm((f) => ({ ...f, stato: aggiornato.stato ?? "" }));
          }}
        />
      )}

      {dettaglio && (
        <Documenti
          key={versioneDocumenti}
          base={`/api/acquisti/contratti/${id}/documenti`}
          puoScrivere={puoScrivere}
          titolo="Documenti contrattuali"
        />
      )}

      {dettaglio && (
        <section className="card text-sm text-gray-600 space-y-1">
          <h2 className="font-semibold text-gray-900">Tracciabilità</h2>
          <p>
            Creato da: <strong>{dettaglio.creatoDa || "—"}</strong> · Modificato
            da: <strong>{dettaglio.modificatoDa || "—"}</strong>
          </p>
          <p>
            Disdetta entro registrata:{" "}
            <strong>{dataIt(dettaglio.dataDisdettaEntro)}</strong>
          </p>
          {dettaglio.garaOrigineId && (
            <p>
              Nato dalla gara:{" "}
              <Link
                to={`/gare/${dettaglio.garaOrigineId}`}
                className="text-acquisti-700 hover:underline"
              >
                apri la gara
              </Link>
            </p>
          )}
          {canPerformAction("gare.gestisci") && (
            <p>
              <Link
                to={`/gare?contratto=${id}`}
                className="text-acquisti-700 hover:underline"
              >
                Apri una gara per il rinnovo di questo contratto →
              </Link>
            </p>
          )}
          {dettaglio.contrattoPrecedente && (
            <p>
              Contratto precedente:{" "}
              <Link
                to={`/contratti/${dettaglio.contrattoPrecedente.id}`}
                className="text-acquisti-700 hover:underline"
              >
                {dettaglio.contrattoPrecedente.nome}
              </Link>
            </p>
          )}
          <p className="text-xs text-gray-500 pt-1">
            «Creato da» e «Modificato da» sono l'utente del portale, non
            l'account di servizio con cui il sistema scrive su Dataverse. Sui 29
            contratti preesistenti sono vuoti: non c'è audit retroattivo.
          </p>
        </section>
      )}
    </form>
  );
}
